import React, { useEffect, useMemo, useState } from 'react';
import { CalendarPlus, Check, Mail, MessageCircle, NotebookPen, Phone, Video } from 'lucide-react';
import { toast } from 'react-toastify';
import { StageDialog, WhenPicker } from './StageKit';
import { apiMessage, completeActivity, createActivity, dueLabel, leadPipelineStages, moveLeadToStage, quickTimes, stageTones } from './crmWork';

// AUTO-CRM-32: one guided form for "what happened, what's next".
// Same outcomes as the phone app's after-call screen (auto-appy-mobile CallOutcome).
const KINDS = [
  { id: 'CALL', label: 'Call', icon: Phone },
  { id: 'WHATSAPP', label: 'WhatsApp', icon: MessageCircle },
  { id: 'MEETING', label: 'Meeting', icon: Video },
  { id: 'EMAIL', label: 'Email', icon: Mail },
  { id: 'NOTE', label: 'Note', icon: NotebookPen },
];
const OUTCOMES = [
  { id: 'interested', label: 'Interested' },
  { id: 'meeting_booked', label: 'Meeting booked' },
  { id: 'call_back_later', label: 'Call back later' },
  { id: 'no_answer', label: 'No answer' },
  { id: 'not_interested', label: 'Not interested' },
  { id: 'wrong_number', label: 'Wrong number' },
];
const NEXT_KINDS = KINDS.filter((kind) => kind.id !== 'NOTE').concat({ id: 'FOLLOWUP', label: 'Other', icon: CalendarPlus });

export default function LogActivityDialog({ lead, mode = 'log', pendingFollowUp = null, onClose, onSaved }) {
  const planOnly = mode === 'plan';
  const [kind, setKind] = useState('CALL');
  const [outcome, setOutcome] = useState(null);
  const [note, setNote] = useState('');
  const [nextKind, setNextKind] = useState('CALL');
  const [nextAt, setNextAt] = useState(() => (planOnly ? quickTimes().find((time) => time.id === 'tomorrow')?.at || null : null));
  const [nextNote, setNextNote] = useState('');
  const [finishPending, setFinishPending] = useState(Boolean(pendingFollowUp));
  const [stages, setStages] = useState([]);
  const [stageId, setStageId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    if (!planOnly && lead?.id) leadPipelineStages(lead.id).then((rows) => { if (active) setStages(rows); }).catch(() => {});
    return () => { active = false; };
  }, [lead?.id, planOnly]);

  // Suggest a sensible next step from the outcome, the way a coach would.
  useEffect(() => {
    if (planOnly || !outcome) return;
    const times = quickTimes();
    if (outcome === 'no_answer') setNextAt(times[0].at);
    else if (outcome === 'call_back_later' || outcome === 'interested') setNextAt(times.find((time) => time.id === 'tomorrow')?.at || null);
    else if (outcome === 'not_interested' || outcome === 'wrong_number') setNextAt(null);
    if (outcome === 'meeting_booked') setNextKind('MEETING');
  }, [outcome, planOnly]);

  const tones = useMemo(() => stageTones(stages), [stages]);
  const currentStageId = lead?.lead_stage_id || lead?.stage?.id;
  const firstName = String(lead?.name || 'this lead').split(/\s+/)[0];

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      if (!planOnly) {
        await createActivity({ leadId: lead.id, type: kind, note, outcome: ['CALL', 'WHATSAPP', 'MEETING'].includes(kind) ? outcome : null });
        if (finishPending && pendingFollowUp) await completeActivity(pendingFollowUp.id);
      }
      if (nextAt) await createActivity({ leadId: lead.id, type: nextKind, note: nextNote, when: nextAt });
      if (!planOnly && stageId && String(stageId) !== String(currentStageId)) await moveLeadToStage(lead.id, stageId);
      toast.success(planOnly ? `Follow-up planned for ${dueLabel(nextAt)}` : nextAt ? `Saved. Next: ${dueLabel(nextAt)}` : 'Saved to the lead’s history');
      window.dispatchEvent(new Event('crm:followups-changed'));
      onSaved?.();
      onClose();
    } catch (saveError) {
      setError(apiMessage(saveError, 'We couldn’t save this. Nothing was lost; try again.'));
    } finally {
      setBusy(false);
    }
  };

  const canSave = planOnly ? Boolean(nextAt) : (kind === 'NOTE' ? note.trim().length > 0 : Boolean(outcome) || note.trim().length > 0);

  return (
    <StageDialog
      title={planOnly ? `Plan a follow-up with ${firstName}` : `What happened with ${firstName}?`}
      subtitle={planOnly ? 'Pick when you’ll get back to them. It shows up in Follow-ups and on Home.' : 'Log it in a few taps, then plan the next step so the lead never goes quiet.'}
      icon={planOnly ? CalendarPlus : NotebookPen}
      onClose={onClose}
      busy={busy}
      width={600}
      footer={(
        <>
          {error ? <p className="crm-kit-error" role="alert">{error}</p> : <span className="ws-muted ws-grow">{!planOnly && !nextAt ? 'Tip: every open lead should have a next follow-up.' : ''}</span>}
          <button type="button" className="ws-btn ws-btn--quiet" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="ws-btn ws-btn--primary" onClick={save} disabled={busy || !canSave}><Check size={15} />{busy ? 'Saving…' : planOnly ? 'Plan follow-up' : 'Save'}</button>
        </>
      )}
    >
      {!planOnly && (
        <>
          <div className="crm-kit-section">
            <span className="crm-kit-label">How did you reach them?</span>
            <div className="crm-chip-row" role="radiogroup" aria-label="Activity type">
              {KINDS.map(({ id, label, icon: Icon }) => (
                <button key={id} type="button" role="radio" aria-checked={kind === id} className={`crm-choice${kind === id ? ' is-on' : ''}`} onClick={() => setKind(id)}><Icon size={14} />{label}</button>
              ))}
            </div>
          </div>
          {kind !== 'NOTE' && kind !== 'EMAIL' && (
            <div className="crm-kit-section">
              <span className="crm-kit-label">How did it go?</span>
              <div className="crm-chip-row" role="radiogroup" aria-label="Outcome">
                {OUTCOMES.map((item) => (
                  <button key={item.id} type="button" role="radio" aria-checked={outcome === item.id} className={`crm-choice${outcome === item.id ? ' is-on' : ''}`} onClick={() => setOutcome(item.id)}>{item.label}</button>
                ))}
              </div>
            </div>
          )}
          <label className="crm-kit-section">
            <span className="crm-kit-label">{kind === 'NOTE' ? 'Note' : 'Notes (optional)'}</span>
            <textarea className="ws-input crm-kit-textarea" rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder={kind === 'NOTE' ? 'What should the team know?' : 'What did they say? Budget, timing, questions…'} maxLength={10000} />
          </label>
          {pendingFollowUp && (
            <label className="crm-kit-check">
              <input type="checkbox" checked={finishPending} onChange={(event) => setFinishPending(event.target.checked)} />
              <span>Mark the planned follow-up ({dueLabel(pendingFollowUp.scheduled_at)}) as done</span>
            </label>
          )}
          <div className="crm-kit-divider" />
        </>
      )}

      <div className="crm-kit-section">
        <span className="crm-kit-label">{planOnly ? 'Follow up by' : 'Next follow-up'}</span>
        <div className="crm-chip-row" role="radiogroup" aria-label="Follow-up type">
          {NEXT_KINDS.map(({ id, label, icon: Icon }) => (
            <button key={id} type="button" role="radio" aria-checked={nextKind === id} className={`crm-choice crm-choice--soft${nextKind === id ? ' is-on' : ''}`} onClick={() => setNextKind(id)}><Icon size={13} />{label}</button>
          ))}
        </div>
      </div>
      <WhenPicker value={nextAt} onChange={setNextAt} label="When" allowNone={!planOnly} />
      <label className="crm-kit-section">
        <span className="crm-kit-label">Reminder note (optional)</span>
        <input className="ws-input crm-kit-input" value={nextNote} onChange={(event) => setNextNote(event.target.value)} placeholder="e.g. Share the revised quote" maxLength={500} />
      </label>

      {!planOnly && stages.length > 0 && (
        <div className="crm-kit-section">
          <span className="crm-kit-label">Move to a stage (optional)</span>
          <div className="crm-chip-row" role="radiogroup" aria-label="Stage">
            {stages.map((stage) => {
              const current = String(stage.id) === String(currentStageId);
              const selected = String(stageId ?? currentStageId) === String(stage.id);
              return (
                <button key={stage.id} type="button" role="radio" aria-checked={selected} className={`crm-choice crm-choice--stage${selected ? ' is-on' : ''}`} style={{ '--tone': tones[stage.id]?.color }} onClick={() => setStageId(current ? null : stage.id)}>
                  <i aria-hidden="true" />{stage.name}{current && <small>now</small>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </StageDialog>
  );
}
